import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

@Component({
  selector: 'ptl-dropdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ptl-dropdown.component.html',
  styleUrls: ['./ptl-dropdown.component.scss'],
})
export class PtlDropdownComponent {
  open = input(false);
  /**
   * Which edge the panel anchors to: `'start'` (default) aligns the panel's left edge with the trigger,
   * extending rightward; `'end'` aligns the panel's right edge with the trigger, extending leftward instead
   * — use this when the trigger sits near the right edge of its container, so the panel doesn't overflow
   * past the viewport and get clipped.
   */
  align = input<'start' | 'end'>('start');
  openChange = output<boolean>();

  private elementRef = inject(ElementRef);
  private readonly wrapper = viewChild<ElementRef<HTMLElement>>('wrapper');
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');
  /** Panel opens upward instead of down when the trigger is close to the bottom of the space it has (e.g. last table row). */
  readonly dropUp = signal(false);

  constructor() {
    // Once the panel is rendered, decide direction: flip up only when it can't fit below but does fit above.
    effect(
      () => {
        if (!this.open()) {
          this.dropUp.set(false);
          return;
        }
        const panel = this.panel()?.nativeElement;
        const wrapper = this.wrapper()?.nativeElement;
        if (!panel || !wrapper) {
          return;
        }
        // The panel is absolute (out of flow), so the wrapper's box tracks the trigger.
        const triggerRect = wrapper.getBoundingClientRect();
        const bounds = this.visibleBounds(wrapper);
        const needed = panel.offsetHeight + 8; // panel height + the 4px gap + a 4px margin
        const spaceBelow = bounds.bottom - triggerRect.bottom;
        const spaceAbove = triggerRect.top - bounds.top;
        // Stay down when it fits below (default). Otherwise flip up if it actually fits above; if it fits
        // neither (tall menu / short viewport), fall to whichever side has more room to minimise clipping.
        const fitsBelow = spaceBelow >= needed;
        const fitsAbove = spaceAbove >= needed;
        this.dropUp.set(!fitsBelow && (fitsAbove || spaceAbove > spaceBelow));
      },
      { allowSignalWrites: true },
    );
  }

  /**
   * How much room the panel actually has, in viewport coordinates. Usually the window — but an absolutely
   * positioned panel is clipped by the nearest scrolling ancestor, so inside one (a table's capped row area,
   * a scrolling side panel) that box is the real limit. Measuring against the window there would keep a menu
   * on the last visible row opening downward, into the edge that cuts it off.
   *
   * Clamped to the window at both ends, which also makes the document element a harmless answer.
   */
  private visibleBounds(from: HTMLElement): { top: number; bottom: number } {
    for (let node = from.parentElement; node; node = node.parentElement) {
      if (getComputedStyle(node).overflowY !== 'visible') {
        const rect = node.getBoundingClientRect();
        return { top: Math.max(rect.top, 0), bottom: Math.min(rect.bottom, window.innerHeight) };
      }
    }
    return { top: 0, bottom: window.innerHeight };
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.elementRef.nativeElement.contains(event.target)) {
      this.openChange.emit(false);
    }
  }
}
